import {useScreenTheme} from '@shared/hooks/useScreenTheme';
import {RootState} from '@shared/store';
import {hideErrorToast} from '@shared/store/errorSlice';
import React, {useMemo, useEffect} from 'react';
import {AccessibilityInfo, View, Text, Platform} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {createErrorToastStyles} from './ErrorToast.styles';
import ErrorCloseIcon from '@shared/assets/icons/ErrorCloseIcon';
import SuccessToastIcon from '@shared/assets/icons/SuccessToastIcon';
import {announceToScreenReader} from './announceForWeb';

/**
 * Minor words that are NOT capitalised in the middle of a title.
 * The first and last word of the message are always capitalised regardless.
 * Follows standard English title-case conventions (APA / Chicago style).
 */
const MINOR_WORDS = new Set([
  // Articles
  'a', 'an', 'the',
  // Coordinating conjunctions
  'and', 'but', 'or', 'nor', 'for', 'so', 'yet',
  // Short prepositions (≤ 3 letters)
  'as', 'at', 'by', 'in', 'of', 'off', 'on', 'per', 'to', 'up', 'via',
]);

/**
 * Grammar-aware title case.
 * Capitalises every word except minor words that appear between the first
 * and last word of the string.
 */
const toTitleCase = (str: string): string => {
  const words = str.split(/(\s+)/);
  // Filter to only actual word tokens (not whitespace chunks) to find boundaries
  const wordTokenIndices = words.reduce<number[]>((acc, token, i) => {
    if (token.trim().length > 0) acc.push(i);
    return acc;
  }, []);
  const firstIdx = wordTokenIndices[0] ?? -1;
  const lastIdx = wordTokenIndices[wordTokenIndices.length - 1] ?? -1;

  return words
    .map((token, i) => {
      // Preserve whitespace tokens unchanged
      if (token.trim().length === 0) return token;
      const lower = token.toLowerCase();
      // Always capitalise the first and last word; capitalise non-minor words
      if (i === firstIdx || i === lastIdx || !MINOR_WORDS.has(lower)) {
        return token.charAt(0).toUpperCase() + token.slice(1);
      }
      return lower;
    })
    .join('');
};

interface ErrorToastProps {
  /**
   * true  (default) — this instance renders only when the toast is global (App.tsx level).
   * false           — this instance renders only when the toast targets the inline page view.
   * After dismiss the slice always resets parentView back to true.
   */
  parentView?: boolean;
}

const ErrorToast = ({parentView = true}: ErrorToastProps) => {
  const dispatch = useDispatch();
  const {
    showErrorToast,
    toastMessage,
    toastId,
    successToast,
    parentView: reduxParentView,
    sessionExpiredMessage,
  } = useSelector((state: RootState) => state.error);
  const theme = useScreenTheme('errorToast');
  const styles = useMemo(
    () => createErrorToastStyles(theme, !!successToast),
    [theme, successToast],
  );

  // Only render when visible, this instance's parentView matches Redux parentView,
  // and the session-expired modal is not currently showing (to avoid overlap).
  const isVisible = showErrorToast && !sessionExpiredMessage && (reduxParentView ?? true) === parentView;

  // Auto-hide toast after 5 seconds. Keyed on toastId (not just showErrorToast)
  // so a new toast fired while a previous one's timer is still counting down
  // restarts the full 5 seconds instead of inheriting the old timer's
  // remaining time (which could hide the new toast almost instantly).
  useEffect(() => {
    if (showErrorToast) {
      const timer = setTimeout(() => {
        dispatch(hideErrorToast());
      }, 5000);

      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showErrorToast, toastId, dispatch]);

  // Announce toast message to screen readers when it becomes visible.
  // RN's accessibilityRole="alert" isn't consistently announced on its own,
  // so we explicitly push the message through the platform's live-region API.
  // Keyed on toastId (not just isVisible/toastMessage) so a repeated dispatch
  // of the identical message — e.g. two consecutive validation failures —
  // still re-announces even though isVisible/toastMessage didn't change value.
  useEffect(() => {
    if (!isVisible || typeof toastMessage !== 'string' || !toastMessage) return;

    if (Platform.OS === 'web') {
      announceToScreenReader(toastMessage);
    } else {
      AccessibilityInfo.announceForAccessibility(toastMessage);
    }
  }, [isVisible, toastMessage, toastId]);

  if (!isVisible) return null;

  return (
    <View
      style={[styles.container, !parentView && styles.childContainer]}
      accessible={false}
      accessibilityRole="none"
      importantForAccessibility="no-hide-descendants"
      aria-hidden={true}>
      {successToast ? (
        <SuccessToastIcon color={theme.successCloseIconColor} />
      ) : (
        <ErrorCloseIcon color={theme.closeIconColor} />
      )}
      <Text style={successToast ? styles.successText: styles.text}>
        {typeof toastMessage === 'string' && toastMessage.length > 0
          ? toTitleCase(toastMessage)
          : toastMessage}
      </Text>

      {/* <TouchableOpacity onPress={() => dispatch(hideErrorToast())}>
        <CloseIcon color={theme.closeCTAColor} />
      </TouchableOpacity> */}
    </View>
  );
};

export default ErrorToast;
